import React, { useContext, useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// contexts
import { useAuth } from '../../../../contexts/Auth';
import { SettingsContext } from '../../../../contexts/Settings';
import { TicketsContext } from '../../../../contexts/Tickets';
// services
import { createVoucher, updateVoucher } from '../../../../services/Ticket';
import { updateInventoryMany } from '../../../../services/Inventory';

TicketVouchersFormDialog.propTypes = {
  openTicketVoucherFormDialog: PropTypes.bool.isRequired,
  setOpenTicketVoucherFormDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object,
  voucher: PropTypes.object
};

export default function TicketVouchersFormDialog({
  openTicketVoucherFormDialog,
  setOpenTicketVoucherFormDialog,
  ticket,
  voucher
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setTickets } = useContext(TicketsContext);

  const initialVoucherValues = useMemo(
    () => ({
      code: '',
      discount: '',
      limit: ''
    }),
    []
  );

  const [voucherValues, setVoucherValues] = useState(initialVoucherValues);

  useEffect(() => {
    if (voucher?._id) {
      setVoucherValues({
        code: voucher.code,
        discount: voucher.discount,
        limit: voucher.limit
      });
    } else {
      setVoucherValues(initialVoucherValues);
    }
  }, [voucher, initialVoucherValues]);

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const VoucherSchema = Yup.object().shape({
    code: Yup.string().required('Code is required'),
    discount: Yup.number().integer().positive().required('Discount is required'),
    limit: Yup.number().integer().positive().required('Limit is required')
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: voucherValues,
    validationSchema: VoucherSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const payload = {
          code: values.code,
          discount: values.discount,
          limit: values.limit
        };

        // TODO: Atualizar usando kafka (provisório)
        const payloadServices = {
          ticket: {
            _id: ticket._id,
            name: ticket.name,
            reference: ticket.reference,
            limit: ticket.limit,
            price: ticket.price,
            voucher: {
              code: values.code,
              discount: values.discount,
              limit: values.limit
            }
          }
        };

        if (voucher?._id) {
          await updateVoucher({
            headers,
            payload,
            eventId: ticket.event._id,
            sectionId: ticket.section._id,
            ticketId: ticket._id,
            voucherId: voucher._id
          });

          payloadServices.ticket.voucher._id = voucher._id;
          payloadServices.ticket.voucher.action = 'update';

          setTickets((tickets) => {
            tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
              if (currentTicket._id === ticket._id) {
                currentTicket.vouchers = currentTicket.vouchers.map((currentVoucher) => {
                  if (currentVoucher._id === voucher._id) {
                    return {
                      ...currentVoucher,
                      code: values.code,
                      discount: values.discount,
                      limit: values.limit
                    };
                  }

                  return currentVoucher;
                });
              }
              return currentTicket;
            });
            return tickets;
          });
        } else {
          const newVoucher = await createVoucher({
            headers,
            payload,
            eventId: ticket.event._id,
            sectionId: ticket.section._id,
            ticketId: ticket._id
          });

          payloadServices.ticket.voucher._id = newVoucher._id;
          payloadServices.ticket.voucher.action = 'create';

          setTickets((tickets) => {
            tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
              if (currentTicket._id === ticket._id) {
                currentTicket.vouchers.push(newVoucher);
              }
              return currentTicket;
            });
            return tickets;
          });
        }

        await updateInventoryMany({ headers, payload: payloadServices });
        handleClose();
        actions.resetForm();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenTicketVoucherFormDialog(false);
  };

  const { errors, touched, getFieldProps, handleSubmit, isSubmitting } = formik;

  return (
    <Dialog open={openTicketVoucherFormDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>Create/Edit Voucher</DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mt: 1 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Code*"
                  {...getFieldProps('code')}
                  error={Boolean(touched.code && errors.code)}
                  helperText={touched.code && errors.code}
                  disabled={Boolean(voucher?.code)}
                />

                <Grid container>
                  <Grid item xs={6} pr={1}>
                    <NumericFormat
                      fullWidth
                      label="Discount (0 - 100%)*"
                      {...getFieldProps('discount')}
                      error={Boolean(touched.discount && errors.discount)}
                      helperText={touched.discount && errors.discount}
                      decimalScale={0}
                      customInput={TextField}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} pl={1}>
                    <NumericFormat
                      fullWidth
                      label="Limit*"
                      {...getFieldProps('limit')}
                      error={Boolean(touched.limit && errors.limit)}
                      helperText={touched.limit && errors.limit}
                      decimalScale={0}
                      customInput={TextField}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
